import { IJSONObject } from "@blings/blings-player";
import { observer } from "mobx-react-lite";
import { FontInput, AccountFontInput } from "../../../API";
import { useState, useCallback, useContext, useMemo, useEffect } from "react";
import { FontFamilySelector } from "./FontFamilySelector";
import { IAccountModel } from "../../../stores/accountStore";
import { AudioOverlay } from "./AudioOverlay";
import { BlingsInput } from "../../../components/BlingsInput/BlingsInput";
import {
  OutlinedColorIcon,
  OutlinedImageIcon,
  OutlinedTextIcon,
  MusicNoteIcon,
  VideoIcon,
  NumberIcon,
  PowerIcon,
  LinkIcon,
} from "../../../icons-and-animations/icons";
import { toTitleCase } from "./helpers";
import { ExtractedValues } from "../../../utils/getTypesFromLiveControl";
import { useDebounceCallback } from "../../../helpers/DebounceHook";
import { RcFile } from "antd/lib/upload/interface";
import { MSTContext } from "../../../stores/Root";
import { ItemTitle } from "./ItemTitle";
import { Checkbox, Input } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import RectColorPicker from "../../../components/RectColorPicker/RectColorPicker";
import "./UseCaseTemplateEditor.scss";
import React from "react";
import { IProjectModel } from "../../../stores/project/projectModel";
import UploadImageAndVideo from "../../../components/UploadImage/UploadImageAndVideo";
const BRANDING_KEY = "Branding";
const EMPTY_GROUP_KEY = "no_group_name";
const LEVEL_0 = "level_0";

interface DescriptionItem {
  description?: string;
}

interface GroupItems {
  [key: string]: DescriptionItem;
}

interface UseCaseGroups {
  [key: string]: GroupItems;
}

interface DestructureGroups {
  [key: string]: UseCaseGroups;
}

type UseCaseTemplateEditorProps = {
  lcsbk: { [key: string]: { [key: string]: ExtractedValues } };
  setFontOverrides: (fontOverrides: FontInput[]) => void;
  useCaseName: string;
  project: IProjectModel;
  account: IAccountModel | undefined;
  setIsSavingTemplate: (isSaving: boolean) => void;
  liveControl: IJSONObject;
  handleLiveControlChange: (
    updatedLiveControl: IJSONObject,
    subGroup: string,
    selectedMenuItem: string
  ) => void;
  fontsInVideoParts: FontInput[];
  fontOverrides: FontInput[];
  overlayAudio: {
    url: string;
    loop: boolean;
    volume: number;
  } | null;
  destructureGroups: DestructureGroups;
  handleOverlayAudioChange: React.Dispatch<
    React.SetStateAction<{
      url: string;
      loop: boolean;
      volume: number;
    }>
  >;
  accountFonts: AccountFontInput[];
  onFontUploadComplete: (newAccountFonts: AccountFontInput[]) => void;
  isUploadingFont: boolean;
  setIsUploadingFont: (isUploading: boolean) => void;
};

// Define the ItemType type
type ItemType =
  | "texts"
  | "images"
  | "colors"
  | "dates"
  | "audio"
  | "numbers"
  | "videos"
  | "booleans"
  | "links";

// Define the ItemTypeValueMap interface
interface ItemTypeValueMap {
  texts: string;
  images: string;
  colors: string;
  dates: string;
  audio: { url: string; loop: boolean; volume: number };
  numbers: string;
  videos: string;
  booleans: boolean;
  links: string;
}

// Define the ItemTypeRenderConfig interface
interface ItemTypeRenderConfig<K extends ItemType> {
  icon: React.ReactNode;
  render: (
    key: string,
    value: ItemTypeValueMap[K],
    index: number,
    groupKey: string
  ) => JSX.Element;
}

export const UseCaseTemplateEditor = observer(
  ({
    fontsInVideoParts,
    fontOverrides,
    setFontOverrides,
    overlayAudio,
    useCaseName,
    account,
    liveControl,
    handleOverlayAudioChange,
    handleLiveControlChange,
    setIsSavingTemplate,
    project,
    lcsbk,
    destructureGroups,
    accountFonts,
    onFontUploadComplete,
    isUploadingFont,
    setIsUploadingFont,
  }: UseCaseTemplateEditorProps) => {
    const { uploadAssetToProject } = useContext(MSTContext).liveControlStore;
    const [isAssetUploading, setIsAssetUploading] = useState(false);
    const [memoAccount, setMemoAccount] = useState<IAccountModel | undefined>(
      undefined
    );

    // // for some reason, the account is not available in the first render, so we need to delay the setting of the memoAccount
    useEffect(() => {
      if (!memoAccount) {
        setMemoAccount(account);
      }
    }, [account]);

    const useCaseNameMemorize = useMemo(() => {
      return useCaseName;
    }, [useCaseName]);

    const handleImageUpload = useCallback(
      (key: string, groupKey: string, fileType: "image" | "video") => {
        return async (
          file: RcFile,
          onSuccess?: (file: File) => void,
          onError?: (error: Error) => void
        ) => {
          try {
            setIsAssetUploading(true);
            setIsSavingTemplate(true);

            await new Promise<void>((resolve, reject) => {
              uploadAssetToProject(
                file,
                {
                  s3Folder: fileType,
                },
                (s3Key: string) => {
                  handleLiveControlChange(
                    { [key]: s3Key },
                    groupKey,
                    useCaseName
                  );
                  onSuccess?.(file as RcFile);
                  resolve();
                }
              );
            });
          } catch (error) {
            onError?.(new Error("Error uploading image"));
          } finally {
            setIsAssetUploading(false);
            setIsSavingTemplate(false);
          }
        };
      },
      [
        useCaseName,
        handleLiveControlChange,
        uploadAssetToProject,
        setIsSavingTemplate,
      ]
    );

    const debouncedHandleColorChange = useCallback(
      useDebounceCallback(
        (key: string, color: string, subGroup: string, useCaseName: string) => {
          handleLiveControlChange({ [key]: color }, subGroup, useCaseName);
        },
        200
      ),
      [handleLiveControlChange]
    );

    const itemTypeConfig: { [K in ItemType]: ItemTypeRenderConfig<K> } =
      useMemo(
        () => ({
          texts: {
            icon: <OutlinedTextIcon />,
            render: (
              key: string,
              value: string,
              index: number,
              groupKey: string
            ) => (
              <div
                className="text-item-container"
                key={`text-item-content-${key}-${index}`}
              >
                <ItemTitle
                  icon={<OutlinedTextIcon />}
                  title={key}
                  description={
                    destructureGroups[useCaseName]?.[groupKey]?.[key]
                      ?.description
                  }
                  className="text-item-title"
                />
                <div className="text-item-content">
                  <BlingsInput
                    value={value}
                    onChange={(newValue: string) =>
                      handleLiveControlChange(
                        { [key]: newValue },
                        groupKey,
                        useCaseName
                      )
                    }
                  />
                </div>
              </div>
            ),
          },
          images: {
            icon: <OutlinedImageIcon />,
            render: (
              key: string,
              value: string,
              index: number,
              groupKey: string
            ) => (
              <div className="image-item-wrapper" key={`image-${index}`}>
                <ItemTitle
                  icon={<OutlinedImageIcon />}
                  title={key}
                  description={
                    destructureGroups[useCaseName]?.[groupKey]?.[key]
                      ?.description
                  }
                  className="image-item-title"
                />
                <UploadImageAndVideo
                  customUploadFile={handleImageUpload(key, groupKey, "image")}
                  onChange={(value: string) => {
                    setIsAssetUploading(true);
                    handleLiveControlChange(
                      { [key]: value },
                      groupKey,
                      useCaseName
                    );
                    setIsAssetUploading(false);
                  }}
                  value={value}
                  isDisabled={isAssetUploading}
                  fileType="image"
                />
              </div>
            ),
          },
          colors: {
            icon: <OutlinedColorIcon />,
            render: (
              key: string,
              value: string,
              index: number,
              groupKey: string
            ) => (
              <div
                className="color-item-container"
                key={`color-item-content-${index}`}
              >
                <ItemTitle
                  icon={<OutlinedColorIcon />}
                  title={key}
                  description={
                    destructureGroups[useCaseName]?.[groupKey]?.[key]
                      ?.description
                  }
                  className="color-item-title"
                />
                <RectColorPicker
                  style={{ width: "180px", display: "flex" }}
                  color={value}
                  colorKey={key}
                  setColorValue={(key: string, color: string) =>
                    debouncedHandleColorChange(
                      key,
                      color,
                      groupKey,
                      useCaseName
                    )
                  }
                />
              </div>
            ),
          },
          dates: {
            icon: null, // No icon for countdown
            render: (
              key: string,
              value: string,
              index: number,
              groupKey: string
            ) => (
              <div key={`countdown-item-content-${index}`}>
                <div className="countdown-item-wrapper">
                  <ItemTitle
                    icon={null}
                    title={key}
                    description={
                      destructureGroups[useCaseName]?.[groupKey]?.[key]
                        ?.description
                    }
                    className="countdown-item-title"
                  />
                  <div className="countdown-item-content">
                    <BlingsInput
                      style={{ height: "31px" }}
                      value={value}
                      date
                      onChange={(newValue: string) =>
                        handleLiveControlChange(
                          { [key]: newValue },
                          groupKey,
                          useCaseName
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ),
          },
          audio: {
            icon: <MusicNoteIcon />,
            render: (
              key: string,
              value: { url: string; loop: boolean; volume: number },
              index: number,
              groupKey: string
            ) => (
              <div key={`audio-item-content-${index}`}>
                <div className="audio-item-wrapper">
                  <ItemTitle
                    icon={<MusicNoteIcon />}
                    title={key}
                    description={
                      destructureGroups[useCaseName]?.[groupKey]?.[key]
                        ?.description
                    }
                    className="audio-item-title"
                  />
                  <AudioOverlay
                    layout="short"
                    overlayAudio={
                      typeof value === "string"
                        ? { url: value, loop: false, volume: 1 }
                        : value
                    }
                    handleOverlayAudioChange={handleOverlayAudioChange}
                  />
                </div>
              </div>
            ),
          },
          numbers: {
            icon: <NumberIcon />,
            render: (
              key: string,
              value: string,
              index: number,
              groupKey: string
            ) => (
              <div
                className="item-container"
                key={`number-item-content-${index}`}
              >
                <ItemTitle
                  icon={<NumberIcon />}
                  title={key}
                  description={
                    destructureGroups[useCaseName]?.[groupKey]?.[key]
                      ?.description
                  }
                  className="number-item-title"
                />
                <div className="number-item-content">
                  <Input
                    type="number"
                    value={value}
                    onChange={(e) =>
                      handleLiveControlChange(
                        { [key]: e.target.value },
                        groupKey,
                        useCaseName
                      )
                    }
                  />
                </div>
              </div>
            ),
          },
          videos: {
            icon: <VideoIcon />,
            render: (
              key: string,
              value: string,
              index: number,
              groupKey: string
            ) => (
              <div
                className="video-item-wrapper"
                key={`video-item-content-${index}`}
              >
                <ItemTitle
                  icon={<VideoIcon />}
                  title={key}
                  description={
                    destructureGroups[useCaseName]?.[groupKey]?.[key]
                      ?.description
                  }
                  className="video-item-title"
                />
                <UploadImageAndVideo
                  customUploadFile={handleImageUpload(key, groupKey, "video")}
                  onChange={(value: string) => {
                    setIsAssetUploading(true);
                    handleLiveControlChange(
                      { [key]: value },
                      groupKey,
                      useCaseName
                    );
                    setIsAssetUploading(false);
                  }}
                  value={value}
                  isDisabled={isAssetUploading}
                  fileType="video"
                />
              </div>
            ),
          },
          booleans: {
            icon: <PowerIcon />,
            render: (
              key: string,
              value: boolean,
              index: number,
              groupKey: string
            ) => (
              <div
                className="item-container"
                key={`boolean-item-content-${index}`}
              >
                <ItemTitle
                  icon={<PowerIcon />}
                  title={key}
                  description={
                    destructureGroups[useCaseName]?.[groupKey]?.[key]
                      ?.description
                  }
                  className="boolean-item-title"
                />
                <div className="boolean-item-content">
                  <Checkbox
                    className="boolean-item-checkbox"
                    checked={value}
                    onChange={(e: CheckboxChangeEvent) =>
                      handleLiveControlChange(
                        { [key]: e.target.checked },
                        groupKey,
                        useCaseName
                      )
                    }
                  />
                </div>
              </div>
            ),
          },
          links: {
            icon: <LinkIcon />,
            render: (
              key: string,
              value: string,
              index: number,
              groupKey: string
            ) => (
              <div
                className="item-container"
                key={`link-item-content-${index}`}
              >
                <ItemTitle
                  icon={<LinkIcon />}
                  title={key}
                  description={
                    destructureGroups[useCaseName]?.[groupKey]?.[key]
                      ?.description
                  }
                  className="link-item-title"
                />
                <div className="link-item-content">
                  <BlingsInput
                    value={value}
                    onChange={(newValue: string) =>
                      handleLiveControlChange(
                        { [key]: newValue },
                        groupKey,
                        useCaseName
                      )
                    }
                  />
                </div>
              </div>
            ),
          },
        }),
        [
          handleLiveControlChange,
          handleImageUpload,
          isAssetUploading,
          debouncedHandleColorChange,
          handleOverlayAudioChange,
          destructureGroups,
          useCaseName,
        ]
      );

    const renderItems = useCallback(
      <K extends ItemType>(
        itemType: K,
        groupKey: string,
        data: IJSONObject
      ) => {
        const config = itemTypeConfig[itemType];
        if (!config) return null;

        return (lcsbk[useCaseName]?.[groupKey][itemType] || []).map(
          (key: string, index: number) => {
            const value = data[key] as ItemTypeValueMap[K];
            return config.render(key, value, index, groupKey);
          }
        );
      },
      [itemTypeConfig, lcsbk, useCaseName]
    );

    if (!lcsbk || !lcsbk?.[useCaseName] || !liveControl) {
      return null;
    }

    return (
      <div className="use-cases-list-wrapper">
        {liveControl &&
          Object.keys(lcsbk?.[useCaseName]).map((key) => {
            const liveControlData =
              key === LEVEL_0
                ? liveControl?.[useCaseName]
                : (liveControl?.[useCaseName] as IJSONObject)?.[key];
            return (
              <div key={key} className="group-elements-wrapper">
                {key !== EMPTY_GROUP_KEY && key !== LEVEL_0 && (
                  <div className="group-elements-title">{toTitleCase(key)}</div>
                )}
                <div className="group-elements-content">
                  {Object.keys(itemTypeConfig).map((itemType) => (
                    <React.Fragment key={itemType}>
                      {renderItems(
                        itemType as ItemType,
                        key,
                        liveControlData as IJSONObject
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          })}
        {useCaseNameMemorize === BRANDING_KEY && (
          <>
            <FontFamilySelector
              account={memoAccount}
              accountFonts={accountFonts}
              fontsInVideoParts={fontsInVideoParts}
              fontOverrides={fontOverrides}
              setFontOverrides={setFontOverrides}
              onUploadComplete={onFontUploadComplete}
              isUploadingFont={isUploadingFont}
              setIsUploadingFont={setIsUploadingFont}
            />
          </>
        )}
      </div>
    );
  }
);
